import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Fade,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Stack,
  Link,
} from "@mui/material";
import {
  createAgentFromIntent,
  listKeys,
  getAgent,
  configureAgent,
  updateAgent,
  deleteAgent,
  listIndexes,
} from "../../api/agentApi";
import { useLocation, useNavigate } from "react-router-dom";
import ExpressAgentReviewOrEdit from "./ExpressAgentReviewOrEdit.tsx";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from "@mui/icons-material/EditOutlined";

const IntentToAgentCreator: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const editingAgentCard = location.state?.agentCard;
  const isEdit = !!editingAgentCard;
  // --- STATE ---
  const [activeStep, setActiveStep] = useState(isEdit ? 1 : 0);
  const [intent, setIntent] = useState<string>("");
  const [agentName, setAgentName] = useState<string>("");
  const [provider, setProvider] = useState<string>("OpenAI");

  const [isDrafting, setIsDrafting] = useState(false);
  const [discoveredTools, setDiscoveredTools] = useState<any[]>([]);
  const [agentResponse, setAgentResponse] = useState<any>(null);
  const [availableKeys, setAvailableKeys] = useState<any[]>([]);

  const [editableAgentName, setEditableAgentName] = useState<string>("");
  const [editableProvider, setEditableProvider] = useState<string>("OpenAI");
  const [editableRole, setEditableRole] = useState<string>("");
  const [editableDescription, setEditableDescription] = useState<string>("");

  const [docIndexes, setDocIndexes] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState<string | null>(null);

  const [toolKeySelections, setToolKeySelections] = useState<{
    [toolName: string]: string;
  }>({});
  const [isCreating, setIsCreating] = useState(false);
  const [pendingAgentId, setPendingAgentId] = useState<string | null>(null); // Track pending agent for manual cleanup
  const [notification, setNotification] = useState<any>({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    listKeys({}).then((res) => setAvailableKeys(res.keys || []));

    // Fetch document indexes for knowledge base selection
    listIndexes("doc")
      .then((res) => {
        setDocIndexes(res.indexes || []);
      })
      .catch(() => setDocIndexes([]));
  }, []);

  // Populate edit mode data immediately (except document index and tool keys)
  useEffect(() => {
    if (isEdit && editingAgentCard) {
      setEditableAgentName(
        editingAgentCard.display_name || editingAgentCard.name || "",
      );
      setEditableProvider(editingAgentCard.llm || "OpenAI");
      setEditableRole(editingAgentCard.role || "");
      setEditableDescription(editingAgentCard.description || "");
      setDiscoveredTools(editingAgentCard.tools || []);
      setAgentResponse(editingAgentCard);
      setPendingAgentId(editingAgentCard.agent_id);
    }
  }, [isEdit, editingAgentCard]);

  // Separate effect to populate tool key selections AFTER availableKeys are loaded
  useEffect(() => {
    if (isEdit && editingAgentCard && availableKeys.length > 0) {
      // Populate toolKeySelections from existing required_keys
      const tools = editingAgentCard.tools || [];
      const keySelections: { [toolName: string]: string } = {};
      
      tools.forEach((tool: any) => {
        if (Array.isArray(tool.required_keys) && tool.required_keys.length > 0) {
          // required_keys is an array of objects like [{ "api_key": "key-id-123" }]
          const keyEntry = tool.required_keys[0];
          // Get the key ID (the value from the first key-value pair)
          const keyId = Object.values(keyEntry)[0] as string;
          if (keyId) {
            keySelections[tool.name] = keyId;
          }
        }
      });
      
      setToolKeySelections(keySelections);
    }
  }, [isEdit, editingAgentCard, availableKeys]);

  // Separate effect to populate document index AFTER docIndexes are loaded
  useEffect(() => {
    if (isEdit && editingAgentCard && docIndexes.length > 0) {
      // Set selected document index if available
      // Check multiple possible locations for index data
      const agentIndexes =
        editingAgentCard.indexes || editingAgentCard.rag || [];

      if (agentIndexes.length > 0) {
        // Handle different index formats
        const indexId =
          agentIndexes[0].index_id || agentIndexes[0].id || agentIndexes[0];
        setSelectedDocIndex(indexId);
      }
    }
  }, [isEdit, editingAgentCard, docIndexes]);

  // NO AUTO-CLEANUP: Removed automatic cleanup on unmount
  // Rationale: Auto-cleanup was too aggressive and caused bugs in happy path
  // Users must explicitly use "Cancel & Discard" button to delete pending agents
  // Orphaned pending agents can be cleaned up manually by admins if needed

  const handleDraftIntent = async () => {
    setIsDrafting(true);
    try {
      const payload: any = {
        intent,
        llm_name: provider,
        display_name: agentName,
        tools_collection: "next_moca_tools",
        max_tools: 10,
      };
      const response = await createAgentFromIntent(payload);
      await new Promise((resolve) => setTimeout(resolve, 500));
      const agentDetails = await getAgent(response.agent_id);
      setAgentResponse(agentDetails);
      setPendingAgentId(agentDetails.agent_id); // Track pending agent
      setDiscoveredTools(agentDetails.tools || []);
      setEditableAgentName(
        agentDetails.display_name || agentDetails.name || agentName,
      );
      setEditableProvider(agentDetails.llm || provider);
      setEditableRole(agentDetails.role || "");
      setEditableDescription(agentDetails.description || "");
      setActiveStep(1);
    } catch (error: any) {
      setNotification({
        open: true,
        message: "Analysis failed",
        severity: "error",
      });
    } finally {
      setIsDrafting(false);
    }
  };

  const handleCreateAgent = async () => {
    setIsCreating(true);
    try {
      const updatedTools = discoveredTools.map((tool) => {
        if (
          Array.isArray(tool.keys_schema) &&
          tool.keys_schema.length > 0 &&
          toolKeySelections[tool.name]
        ) {
          return {
            ...tool,
            required_keys: [
              { [tool.keys_schema[0].name]: toolKeySelections[tool.name] },
            ],
          };
        }
        return tool;
      });

      if (isEdit) {
        // Edit mode: Use PUT /agent/{id} to create a new version
        const agentCard = {
          name: agentResponse.name,
          display_name: editableAgentName,
          description: editableDescription,
          role: editableRole,
          llm: editableProvider,
          tools: updatedTools,
          document_index_id: selectedDocIndex,
        };
        await updateAgent(agentResponse.agent_id, agentCard);
      } else {
        // Create mode: Use PUT /agent/{id}/configure (no new version)
        await configureAgent(
          agentResponse.agent_id,
          updatedTools,
          agentResponse.version,
          editableAgentName,
          editableProvider,
          editableRole,
          editableDescription,
          selectedDocIndex, // Pass document index ID
        );
      }

      // Success - navigate to agent detail page
      setPendingAgentId(null);
      navigate(`/agent/${agentResponse.agent_id}`);
    } catch (error: any) {
      setNotification({
        open: true,
        message: `${isEdit ? "Update" : "Build"} failed: ${error.response?.data?.detail || error.message}`,
        severity: "error",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelCreation = async () => {
    if (isEdit) {
      // In edit mode, just navigate back to agent detail
      navigate(`/agent/${agentResponse.agent_id}`);
      return;
    }

    if (pendingAgentId) {
      try {
        await deleteAgent(pendingAgentId);
        setPendingAgentId(null);
        setAgentResponse(null);
        setDiscoveredTools([]);
        setActiveStep(0);
        setNotification({
          open: true,
          message: "Agent draft discarded",
          severity: "info",
        });
      } catch (error: any) {
        setNotification({
          open: true,
          message: "Failed to discard agent",
          severity: "error",
        });
      }
    } else {
      setActiveStep(0);
    }
  };

  const handleIdentityChange = (
    field: "name" | "provider" | "role" | "description",
    value: string,
  ) => {
    switch (field) {
      case "name":
        setEditableAgentName(value);
        break;
      case "provider":
        setEditableProvider(value);
        break;
      case "role":
        setEditableRole(value);
        break;
      case "description":
        setEditableDescription(value);
        break;
    }
  };

  const handleToolKeySelect = (toolName: string, keyId: string) => {
    setToolKeySelections({
      ...toolKeySelections,
      [toolName]: keyId,
    });
  };

  const toolsRequireKeys = discoveredTools.some(
    (t) => Array.isArray(t.keys_schema) && t.keys_schema.length > 0,
  );
  const allKeysSelected = discoveredTools
    .filter((t) => Array.isArray(t.keys_schema) && t.keys_schema.length > 0)
    .every((t) => toolKeySelections[t.name]);

  const handleFullEdit = () => {
    // Always pass the latest card as state for pre-population
    navigate("/agent/conversational", {
      state: { agentCard: editingAgentCard },
    });
  };

  const handleFullAgentCreate = () => {
    // Always pass the latest card as state for pre-population
    navigate("/agent/conversational");
  };

  return (
    <Box sx={{ flex: 1, overflow: "auto", px: 3, pt: 2 }}>
      <Box>
        <Typography variant="h4">
          {isEdit ? "Edit Agent" : "Create Agent"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, px: 0.5 }}
        >
          {isEdit
            ? "Update your agent's metadata, knowledge collection, and tools."
            : "Provide your agent's intent to instantly draft its role, and required capabilities."}
        </Typography>
        {!isEdit && (
          <Link
            component="button"
            onClick={handleFullAgentCreate}
            sx={{ ml: 0.5, fontWeight: 700, verticalAlign: "baseline" }}
          >
            Full Step-by-Step Agent Creator
          </Link>
        )}
        {isEdit && (
          <Link
            component="button"
            onClick={handleFullEdit}
            sx={{ ml: 0.5, fontWeight: 700, verticalAlign: "baseline" }}
          >
            Full Step-by-Step Editor
          </Link>
        )}
      </Box>

      {/* COMPACT STEPPER - Hide in edit mode */}
      {!isEdit && (
        <Box
          sx={{ mb: 4, mt: 3, display: "flex", justifyContent: "flex-start" }}
        >
          <Stepper
            activeStep={activeStep}
            sx={{ width: "100%", maxWidth: "500px" }}
          >
            <Step>
              <StepLabel>Draft</StepLabel>
            </Step>
            <Step>
              <StepLabel>Review</StepLabel>
            </Step>
          </Stepper>
        </Box>
      )}

      {/* STEP 1: DEFINE INTENT - Hide in edit mode */}
      {!isEdit && activeStep === 0 && (
        <Fade in={activeStep === 0}>
          <Box>
            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 2,
                mb: 2,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <Card
                sx={{
                  p: 2,
                  borderRadius: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, ml: 1, fontWeight: 700 }}
                >
                  Agent Intent
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="e.g. 'Generate image for a given prompt...'"
                  value={intent}
                  onChange={(e) => setIntent(e.target.value)}
                  variant="filled"
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                />
              </Card>
            </Paper>
            <Box sx={{ mt: 4, display: "flex", justifyContent: "right" }}>
              <Button
                size="large"
                variant="contained"
                color="primary"
                onClick={handleDraftIntent}
                disabled={isDrafting || !intent.trim()}
                startIcon={
                  isDrafting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    isEdit ?  <EditIcon /> : <AddCircleOutlineIcon />
                  )
                }
                sx={{
                  borderRadius: 99,
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  boxShadow: "0 10px 30px rgba(25, 118, 210, 0.3)",
                }}
              >
                {isDrafting ? "Drafting..." : "Draft"}
              </Button>
            </Box>
          </Box>
        </Fade>
      )}

      {/* STEP 2: REVIEW & LAUNCH - Always show in edit mode, otherwise show on step 1 */}
      {(isEdit || activeStep === 1) && (
        <Box sx={{ mt: isEdit ? 3 : 0 }}>
          <ExpressAgentReviewOrEdit
            identity={{
              name: editableAgentName,
              provider: editableProvider,
              role: editableRole,
              description: editableDescription,
            }}
            onIdentityChange={handleIdentityChange}
            knowledgeBase={{
              docIndexes,
              selectedDocIndex,
            }}
            onKnowledgeBaseChange={setSelectedDocIndex}
            tools={{
              discoveredTools,
              toolKeySelections,
              availableKeys,
            }}
            onToolKeySelect={handleToolKeySelect}
            onCancel={handleCancelCreation}
            onCreate={handleCreateAgent}
            isCreating={isCreating}
            toolsRequireKeys={toolsRequireKeys}
            allKeysSelected={allKeysSelected}
            isEditMode={isEdit}
          />
        </Box>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntentToAgentCreator;
